/**
 * Quiet Hours Card Component
 * Timezone selection and notification time range
 */

import { Moon, Info } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { TIMEZONES, formatHour } from '../utils/constants';

interface QuietHoursCardProps {
  timezone: string;
  windowStart: number;
  windowEnd: number;
  isLoading: boolean;
  onChangeTimezone: (timezone: string) => void;
  onChangeWindowStart: (hour: number) => void;
  onChangeWindowEnd: (hour: number) => void;
}

export function QuietHoursCard({
  timezone,
  windowStart,
  windowEnd,
  isLoading,
  onChangeTimezone,
  onChangeWindowStart,
  onChangeWindowEnd,
}: QuietHoursCardProps) {
  const { t } = useLanguage();

  // Calculate quiet hours display
  // windowStart and windowEnd define when notifications ARE allowed
  // So quiet hours are OUTSIDE this window
  const quietStart = windowEnd;
  const quietEnd = windowStart;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Moon className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">
              {t('notifications.quietHours.title', 'Quiet Hours')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('notifications.quietHours.subtitle', "Set times when you won't receive notifications")}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Timezone selector */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            {t('notifications.quietHours.timezone', 'Timezone')}
          </label>
          <select
            value={timezone}
            onChange={e => onChangeTimezone(e.target.value)}
            disabled={isLoading}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:bg-gray-100"
          >
            {TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>

        {/* Time range sliders */}
        <div className="space-y-4">
          {/* Start time */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                {t('notifications.quietHours.startTime', 'Start Time')}
              </label>
              <span className="text-sm font-semibold text-primary-600">
                {formatHour(windowStart)}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={23}
              step={1}
              value={windowStart}
              onChange={e => onChangeWindowStart(Number(e.target.value))}
              disabled={isLoading}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600 disabled:opacity-50"
            />
          </div>

          {/* End time */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                {t('notifications.quietHours.endTime', 'End Time')}
              </label>
              <span className="text-sm font-semibold text-primary-600">
                {formatHour(windowEnd)}
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={23}
              step={1}
              value={windowEnd}
              onChange={e => onChangeWindowEnd(Number(e.target.value))}
              disabled={isLoading}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600 disabled:opacity-50"
            />
          </div>
        </div>

        {/* Preview box */}
        <div className="p-4 bg-purple-50 border border-purple-100 rounded-xl">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-purple-800">
                {t('notifications.quietHours.allowed', 'Notifications allowed')}
              </div>
              <p className="text-sm text-purple-700 mt-1">
                {formatHour(windowStart)} - {formatHour(windowEnd)}
              </p>
              {windowStart !== windowEnd && (
                <p className="text-xs text-purple-600 mt-2">
                  {t(
                    'notifications.quietHours.paused',
                    `Quiet from ${formatHour(quietStart)} to ${formatHour(quietEnd)}`
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
